<template>
  <div class="kf-strip neumorphism-flat">
    <template v-if="loading">
      <div v-for="n in items.length || 3" :key="'s' + n" class="kf-strip__cell">
        <v-skeleton-loader type="list-item-two-line" class="kf-strip__skeleton" />
      </div>
    </template>

    <template v-else>
      <!-- A <button> only when the caller gave the item a `value`; otherwise a
           plain div, so a read-only strip stays unclickable and keyboard focus
           does not stop on it. -->
      <component
        :is="interactive ? 'button' : 'div'"
        v-for="stat in items"
        :key="stat.label"
        class="kf-strip__cell"
        :class="{
          'kf-strip__cell--btn': interactive,
          'kf-strip__cell--active': interactive && isActive(stat),
        }"
        :type="interactive ? 'button' : null"
        :aria-pressed="interactive ? String(isActive(stat)) : null"
        @click="onSelect(stat)"
      >
        <!-- The label is nowrap + ellipsis, and the cells split the width
             evenly, so a strip with many cells clips the longer names. The
             title makes the full one readable on hover. -->
        <span class="kf-strip__label" :title="stat.label">{{ stat.label }}</span>
        <span class="kf-strip__value" :title="rawTitle(stat)">
          {{ display(stat) }}
        </span>
      </component>
    </template>
  </div>
</template>

<script>
import fmt from "@/app-js/karrofin/format";

/**
 * A row of summary figures shown as ONE bar rather than as separate cards.
 *
 * Same job as KpiCard, different shape: where KpiCard is a tile in a grid and
 * tints its icon per metric, this is a single surface split by rules and drawn
 * entirely in --kf-brand. Nothing here is colour-coded, so the figures read as
 * three views of one number rather than as three unrelated things.
 *
 * Horizontal from `sm` up; below that the cells stack and the dividers turn
 * horizontal, because three numbers side by side on a phone are unreadable.
 */
export default {
  name: "KfStatStrip",
  props: {
    /**
     * [{ label, value, isCurrency, isText }] -- order is the display order.
     *
     * `isText` passes the value straight through. Without it every value goes
     * through the number formatter, which turns something like
     * "10.5% - 24% p.a." into a dash -- fine for the counts this started out
     * showing, wrong for a product's terms.
     */
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    /**
     * Which segment's `filter` is currently applied; null when none is.
     *
     * Must stay declared. Left undeclared it lands in $attrs rather than on the
     * instance, and every comparison against it silently fails: the selected
     * segment never gets its --active class, aria-pressed sticks at "false",
     * and onSelect can no longer recognise a second press as "clear this" --
     * so a filter, once chosen, could not be switched off.
     */
    active: { type: String, default: null },
  },
  computed: {
    /** Interactive only when the caller actually supplied filter values. */
    interactive() {
      return this.items.some((s) => "filter" in s);
    },
  },
  methods: {
    isActive(stat) {
      return (stat.filter === undefined ? null : stat.filter) === this.active;
    },
    onSelect(stat) {
      if (!this.interactive) return;
      const value = stat.filter === undefined ? null : stat.filter;
      // Clicking the active segment clears it, the same toggle the stage tabs
      // on the other screens use.
      this.$emit("select", value === this.active ? null : value);
    },
    display(stat) {
      if (stat.isText) return stat.value;
      return stat.isCurrency ? fmt.currencyShort(stat.value) : fmt.number(stat.value);
    },
    /** Full precision on hover, since currency values are abbreviated. */
    rawTitle(stat) {
      if (stat.isText) return stat.value;
      return stat.isCurrency ? fmt.currency(stat.value) : fmt.number(stat.value);
    },
  },
};
</script>

<style scoped>
.kf-strip {
  display: flex;
  flex-wrap: nowrap;
  margin-bottom: 12px;
  overflow: hidden;
}
.kf-strip__cell {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 14px 18px;
}
/* Rules between cells only -- a border on every cell would double up against
   the container's own border on the outer edges. */
.kf-strip__cell + .kf-strip__cell {
  border-left: 1px solid var(--kf-border);
}
/* Interactive segments. The :not(--active) guard is load-bearing: `:hover`
   outranks a plain class, so without it hovering the selected segment would
   repaint it pale and leave its white text invisible. */
.kf-strip__cell--btn {
  cursor: pointer;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  transition: background 0.15s ease, color 0.15s ease;
  outline: none;
}
.kf-strip__cell--btn:hover:not(.kf-strip__cell--active) {
  background: var(--kf-hover);
}
.kf-strip__cell--active {
  background: var(--kf-brand);
}
.kf-strip__cell--active .kf-strip__label,
.kf-strip__cell--active .kf-strip__value {
  color: #ffffff;
}
.kf-strip__cell--active:hover {
  background: var(--kf-brand-strong);
}
.kf-strip__label {
  font-size: var(--kf-fs-meta);
  font-weight: 600;
  color: #000;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kf-strip__value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.15;
  color: var(--kf-brand);
}
.kf-strip__skeleton {
  width: 100%;
  background: transparent;
}

@media (max-width: 599px) {
  .kf-strip {
    flex-direction: column;
  }
  .kf-strip__cell {
    padding: 12px 16px;
  }
  .kf-strip__cell + .kf-strip__cell {
    border-left: none;
    border-top: 1px solid var(--kf-border);
  }
  .kf-strip__value {
    font-size: 22px;
  }
}
</style>
